import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

function About() {
  const { t } = useTranslation();

  const leaders = [
    {
      name: t('about.leader1Name'),
      role: t('about.leader1Role'),
      bio: t('about.leader1Bio'),
      image: '/assets/images/pastor.jpeg',
    },
    {
      name: t('about.leader2Name'),
      role: t('about.leader2Role'),
      bio: t('about.leader2Bio'),
      image: '/assets/images/mama-pastor.jpeg',
    },
    {
      name: t('about.leader3Name'),
      role: t('about.leader3Role'),
      bio: t('about.leader3Bio'),
      image: '/assets/images/simi.jpg',
    },
    {
      name: t('about.leader4Name'),
      role: t('about.leader4Role'),
      bio: t('about.leader4Bio'),
      image: '/assets/images/jovia.jpeg',
    },
    {
      name: t('about.leader5Name'),
      role: t('about.leader5Role'),
      bio: t('about.leader5Bio'),
      image: '/assets/images/lumumba.jpeg',
    },
  ];

  const faithPoints = [
    {
      text: t('about.faithPoint1'),
      icon: (
        <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
        </svg>
      ),
    },
    {
      text: t('about.faithPoint2'),
      icon: (
        <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5s3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18s-3.332.477-4.5 1.253"/>
        </svg>
      ),
    },
    {
      text: t('about.faithPoint3'),
      icon: (
        <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      ),
    },
    {
      text: t('about.faithPoint4'),
      icon: (
        <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      ),
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
  };

  return (
    <div>
      <Helmet>
        <title>{t('about.seoTitle')}</title>
        <meta name="description" content={t('about.seoDescription')} />
      </Helmet>

      {/* ==================== HERO ==================== */}
      <section className="relative h-[420px] md:h-[480px] flex items-center justify-center text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/assets/images/about1.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/30" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
            {t('about.heroTitle')}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            {t('about.heroSubtitle')}
          </p>
        </motion.div>
      </section>

      {/* ==================== LEADERSHIP ==================== */}
      <section className="bg-secondary py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-3">
              {t('about.leadershipTitle')}
            </h2>
            <p className="text-textPrimary/70 max-w-2xl mx-auto">
              Meet the dedicated leaders serving the vision of NAOGM
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {leaders.map((leader, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center"
              >
                <div className="relative w-28 h-28 mx-auto mb-5">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full rounded-full object-cover border-4 border-accent/30"
                    onError={(e) =>
                      (e.currentTarget.src = 'https://via.placeholder.com/150?text=Leader')
                    }
                  />
                </div>
                <h3 className="text-lg font-bold text-textPrimary">{leader.name}</h3>
                <p className="text-accent font-medium text-sm mt-1">{leader.role}</p>
                <p className="text-sm text-textPrimary/70 mt-3 leading-relaxed">
                  {leader.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== STATEMENT OF FAITH ==================== */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-3">
              {t('about.faithTitle') || 'What We Believe'}
            </h2>
            <p className="text-textPrimary/70 max-w-2xl mx-auto">
              Our foundation is built on the unchanging truth of God’s Word
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faithPoints.map((point, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="flex gap-5 p-6 rounded-2xl bg-secondary hover:bg-secondary/80 transition-colors duration-300"
              >
                <div className="shrink-0 w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center">
                  {point.icon}
                </div>
                <p className="text-textPrimary/90 leading-relaxed pt-2">
                  {point.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-blue-800" />
        
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
              We would love to connect with you and share more about the work God is doing.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-accent hover:bg-yellow-500 text-white px-8 py-3.5 rounded-xl text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              {t('about.ctaButton')}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default About;